/**
 * Pure helper for the FilterableColumnHeader async-search dropdown — what
 * options to render given the current pre-fetched list, async results,
 * selected ids, and search term.
 *
 * Extracted from the component so the search-vs-no-search semantics can be
 * unit-tested without rendering React. The QA-flagged bug came from this
 * exact logic: typing "time" showed every pre-fetched workflow on top of
 * the empty search-results list, because the helper used to do
 * `[...filterOptions, ...newResults]` unconditionally.
 */
import type { FilterOption } from './index'

interface ComputeArgs {
  filterOptions: FilterOption[]
  asyncOptions: FilterOption[]
  selectedFilters: string[]
  searchTerm: string
}

export function computeAsyncDisplayOptions({
  filterOptions,
  asyncOptions,
  selectedFilters,
  searchTerm,
}: ComputeArgs): FilterOption[] {
  // No active search → render the pre-fetched options as-is. They're the
  // top-N facet results from /filters and naturally include whatever the
  // user has already selected.
  if (!searchTerm) return filterOptions

  // Active search → render the union of:
  //   1. Pre-fetched options that ALSO match the search term, client-side
  //      (so options the parent already had — e.g. the records-based
  //      fallback in TableView, or any selected item — survive the search).
  //   2. Async results from the backend.
  //   3. Currently-selected items, always (so the user can uncheck them
  //      mid-search even if they don't match the query).
  // Deduped by `value`. The QA-bug guard from before still holds: pre-fetched
  // options that DON'T match the search are excluded — typing "time" no
  // longer leaks "Bill Approval form" through.
  const selectedIds = new Set(selectedFilters)
  const needle = searchTerm.toLowerCase()
  const prefetchedKept = filterOptions.filter(
    (o) => selectedIds.has(o.value) || o.label.toLowerCase().includes(needle),
  )
  const seen = new Set(prefetchedKept.map((o) => o.value))
  const asyncKept = asyncOptions.filter((o) => !seen.has(o.value))
  return [...prefetchedKept, ...asyncKept]
}
